import React from 'react';
import { EpisodeStatus } from '@/types/episode.types';
import { ClipStatus } from '@/types/clip.types';
import { Badge } from '@/components/ui/badge';
import { Clock } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface StatusBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  status: EpisodeStatus | ClipStatus | string; // Accept episode, clip, or legacy statuses
  size?: 'default' | 'lg';
  scheduledPublishDate?: Date | string | null; // Optional: show clock icon if scheduled for future
}

// Legacy status mapping for backwards compatibility
// Note: 'draft' is NOT mapped because DRAFT is now a first-class status in the clip-based workflow
type LegacyStatus = 'processing' | 'ready' | 'failed';
const LEGACY_STATUS_MAP: Record<LegacyStatus, EpisodeStatus> = {
  'processing': EpisodeStatus.GENERATING_AUDIO,  // Old processing -> now generating_audio
  'ready': EpisodeStatus.AUDIO_READY,            // Old ready -> now audio_ready
  'failed': EpisodeStatus.AUDIO_FAILED,          // Old failed -> now audio_failed
};

/**
 * Episode Status Badge
 * Uses shadcn Badge with color-coordinated styling
 *
 * Workflow: Script Generation → Audio Generation → Publishing
 *
 * Color Mapping:
 * - draft: Gray (#6b7280)
 * - clips added / generating: Amber (#f59e0b)
 * - scripts pending review: Orange (#f97316)
 * - scripts approved: Cyan (#00c8b4)
 * - audio ready/approved: Teal (#14b8a6)
 * - merged: Blue (#3b82f6)
 * - published: Green (#22c55e)
 * - failed states: Red
 */
const STATUS_CONFIG: Record<EpisodeStatus, { variant: string; label: string; }> = {
  // ============================================
  // CLIP-BASED WORKFLOW STATUSES (NEW)
  // ============================================
  [EpisodeStatus.DRAFT]: {
    variant: 'secondary',
    label: 'Draft',
  },
  [EpisodeStatus.GENERATING_SCRIPTS]: {
    variant: 'processing',
    label: 'Generating Scripts',
  },
  [EpisodeStatus.SCRIPTS_PENDING_REVIEW]: {
    variant: 'available',
    label: 'Scripts Ready',
  },
  [EpisodeStatus.SCRIPTS_APPROVED]: {
    variant: 'approved',
    label: 'Scripts Approved',
  },
  [EpisodeStatus.AUDIO_PENDING_REVIEW]: {
    variant: 'ready',
    label: 'Audio Ready',
  },
  [EpisodeStatus.AUDIO_APPROVED]: {
    variant: 'ready',
    label: 'Audio Approved',
  },
  [EpisodeStatus.MERGING]: {
    variant: 'processing',
    label: 'Merging',
  },
  [EpisodeStatus.MERGED]: {
    variant: 'merged',
    label: 'Merged',
  },
  [EpisodeStatus.FAILED]: {
    variant: 'destructive',
    label: 'Failed',
  },

  // ============================================
  // LEGACY SINGLE-ARTICLE WORKFLOW STATUSES
  // ============================================
  [EpisodeStatus.PENDING]: {
    variant: 'secondary',
    label: 'Pending',
  },
  [EpisodeStatus.GENERATING_SCRIPT]: {
    variant: 'processing',
    label: 'Generating Script',
  },
  [EpisodeStatus.SCRIPT_READY]: {
    variant: 'available',
    label: 'Script Ready',
  },
  [EpisodeStatus.SCRIPT_FAILED]: {
    variant: 'destructive',
    label: 'Script Failed',
  },
  [EpisodeStatus.GENERATING_AUDIO]: {
    variant: 'processing',
    label: 'Generating Audio',
  },
  [EpisodeStatus.AUDIO_READY]: {
    variant: 'ready',
    label: 'Audio Ready',
  },
  [EpisodeStatus.AUDIO_FAILED]: {
    variant: 'destructive',
    label: 'Audio Failed',
  },

  // ============================================
  // SHARED STATUSES
  // ============================================
  [EpisodeStatus.PUBLISHING]: {
    variant: 'processing',
    label: 'Publishing',
  },
  [EpisodeStatus.PUBLISHED]: {
    variant: 'published',
    label: 'Published',
  },
  [EpisodeStatus.PUBLISHING_FAILED]: {
    variant: 'destructive',
    label: 'Publish Failed',
  },
  [EpisodeStatus.ARCHIVED]: {
    variant: 'secondary',
    label: 'Archived',
  },
};

// Clip status configuration - single source of truth for clip badge styling
// Uses same color scheme as episode statuses
const CLIP_STATUS_CONFIG: Record<ClipStatus, { variant: string; label: string }> = {
  pending: { variant: 'secondary', label: 'Pending' },
  script_queued: { variant: 'queued', label: 'Script Queued' },
  generating_script: { variant: 'processing', label: 'Generating Script' },
  script_ready: { variant: 'available', label: 'Script Ready' },       // Orange
  script_failed: { variant: 'destructive', label: 'Script Failed' },
  script_approved: { variant: 'approved', label: 'Script Approved' },  // Cyan
  audio_queued: { variant: 'queued', label: 'Audio Queued' },
  generating_audio: { variant: 'processing', label: 'Generating Audio' },
  audio_ready: { variant: 'ready', label: 'Audio Ready' },             // Teal
  audio_failed: { variant: 'destructive', label: 'Audio Failed' },
  audio_approved: { variant: 'published', label: 'Approved' },         // Green
};

// All valid clip status values for type checking
const CLIP_STATUSES: ClipStatus[] = [
  'pending', 'script_queued', 'generating_script', 'script_ready', 'script_failed',
  'script_approved', 'audio_queued', 'generating_audio', 'audio_ready', 'audio_failed', 'audio_approved'
];

// Helper to convert various date formats to Date object
// Handles: Date, ISO string, Firestore Timestamp object { _seconds, _nanoseconds }, or { seconds, nanoseconds }
// Returns null for invalid dates to prevent RangeError in date formatting
function toDate(value: unknown): Date | null {
  if (!value) return null;

  let date: Date | null = null;

  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string') {
    date = new Date(value);
  } else if (typeof value === 'number') {
    date = new Date(value);
  } else if (typeof value === 'object') {
    // Handle Firestore Timestamp objects (from WebSocket JSON)
    const obj = value as Record<string, unknown>;
    // Firestore timestamps come as { _seconds, _nanoseconds } or { seconds, nanoseconds }
    const seconds = (obj._seconds ?? obj.seconds) as number | undefined;
    if (typeof seconds === 'number') {
      date = new Date(seconds * 1000);
    }
  }

  // Validate the date - return null if invalid to prevent RangeError
  if (date && !isNaN(date.getTime())) {
    return date;
  }
  return null;
}

// Helper to check if a date is in the future
function isScheduledForFuture(scheduledDate: unknown): boolean {
  const date = toDate(scheduledDate);
  if (!date) return false;
  return date > new Date();
}

// Format scheduled date for tooltip
function formatScheduledDate(scheduledDate: unknown): string {
  const date = toDate(scheduledDate);
  if (!date) return '';
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, size = 'default', scheduledPublishDate, ...props }) => {
  // Size classes
  const sizeClasses = size === 'lg'
    ? 'px-3 py-1 text-sm'
    : '';

  // Check if it's a clip status
  if (CLIP_STATUSES.includes(status as ClipStatus)) {
    const clipConfig = CLIP_STATUS_CONFIG[status as ClipStatus];
    return (
      <Badge variant={clipConfig.variant as any} className={sizeClasses} {...props}>
        {clipConfig.label}
      </Badge>
    );
  }

  // Handle episode statuses
  let normalizedStatus: EpisodeStatus = status as EpisodeStatus;

  // Check if it's a legacy status
  if (status in LEGACY_STATUS_MAP) {
    normalizedStatus = LEGACY_STATUS_MAP[status as LegacyStatus];
    console.warn(`[StatusBadge] Legacy status "${status}" mapped to "${normalizedStatus}"`);
  }

  const config = STATUS_CONFIG[normalizedStatus];

  // Fallback for unknown status values
  if (!config) {
    console.warn(`Unknown status: ${status}`);
    return (
      <Badge variant="secondary" className={sizeClasses} {...props}>
        {status || 'Unknown'}
      </Badge>
    );
  }

  // Check if this is a published episode scheduled for the future
  const isScheduled = normalizedStatus === EpisodeStatus.PUBLISHED && isScheduledForFuture(scheduledPublishDate);

  // Render badge with optional scheduled indicator
  const badge = (
    <Badge variant={config.variant as any} className={sizeClasses} {...props}>
      {config.label}
    </Badge>
  );

  if (isScheduled && scheduledPublishDate) {
    return (
      <div className="flex items-center gap-1.5">
        {badge}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Clock className="h-4 w-4 text-amber-500" />
            </TooltipTrigger>
            <TooltipContent>
              <p>Scheduled for {formatScheduledDate(scheduledPublishDate)}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  return badge;
};